/**
 * Marks an error as "the merchant has been told about this".
 *
 * Deliberately dependency-free and separate from the React provider that sets
 * it: `instrumentation-client.ts` reads it inside Sentry.init, and importing a
 * 'use client' component there would pull React and react-query into the
 * instrumentation bundle.
 *
 * Why it exists: a tRPC mutation that fails a business rule — "Your trial has
 * ended or your subscription is inactive" — is not a crash, but `mutateAsync`
 * rejects and an async click handler has nothing to catch it, so it reached
 * Sentry via onunhandledrejection tagged `handled: no`. Flagging the error when
 * its message goes on screen lets beforeSend drop exactly those, without
 * matching on message text that copy changes or translation would break.
 */

const SURFACED = Symbol.for('yp.surfacedToMerchant')

/** Call once the error's message is actually visible to the merchant. */
export function markSurfaced(err: unknown): void {
  if (err && typeof err === 'object') {
    try {
      ;(err as Record<symbol, unknown>)[SURFACED] = true
    } catch {
      // Frozen or exotic error object. Nothing to do — the event still gets
      // reported, which is the safe direction to fail in.
    }
  }
}

export function wasSurfaced(err: unknown): boolean {
  return !!(err && typeof err === 'object' && (err as Record<symbol, unknown>)[SURFACED] === true)
}
